import React from 'react';
import { Link } from 'react-router-dom';
import Text from '@amzn/meridian/text';
import FooterItem from 'src/components/app/footer/footer-item/FooterItem';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import * as AppRoutes from 'src/routing/constants/routes';
import { lockZoomOnSmallScreens } from 'src/utils/viewport-scaling';
import { PageChrome, useSetPageChrome } from 'src/components/app/page-chrome/PageChrome';
import '../Unload.scss';
import '../unload-footer/UnloadFooter.scss';
import './UnloadChooserPage.scss';

interface ChooserOption {
    // Its data-testid
    testId: string;
    // The page it opens
    to: string;
    // Its name
    name: string;
    // The line under the name
    line: string;
}

// The tool that shows Receive, as it shows the top bar's Receive button
const RECEIVE_TOOL = 'Receive';

const RECEIVE: ChooserOption = { testId: 'unload-chooser-receive', to: AppRoutes.RECEIVE_PAGE_ROUTE,
    name: AppStrings.RECEIVE, line: AppStrings.UNLOAD_CHOOSER_RECEIVE_LINE };
// Open to every associate, as their routes are
const UNLOAD_OPTIONS: ChooserOption[] = [
    { testId: 'unload-chooser-trailer', to: AppRoutes.UNLOAD_TRAILER_PAGE_ROUTE,
        name: AppStrings.UNLOAD_CHOOSER_TRAILER, line: AppStrings.UNLOAD_CHOOSER_TRAILER_LINE },
    { testId: 'unload-chooser-pallet', to: AppRoutes.UNLOAD_PALLET_PAGE_ROUTE, name: AppStrings.UNLOAD_CHOOSER_PALLET,
        line: AppStrings.UNLOAD_CHOOSER_PALLET_LINE },
    { testId: 'unload-chooser-resolve-sideline', to: AppRoutes.UNLOAD_RESOLVE_PAGE_ROUTE,
        name: AppStrings.UNLOAD_RESOLVE_SIDELINE_HEADING, line: AppStrings.UNLOAD_CHOOSER_RESOLVE_SIDELINE_LINE },
];

// The unload steps' top bar and side menu, over RIV Home alone. Its own footer, not the steps', so a tap on a step's
// Back leaves no focused link here for a scanner's Enter to follow home
const CHROME: PageChrome = { compact: true, footer: (
    <div className="footer-container unload-footer" data-testid="footer">
        <Link to={AppRoutes.HOME_PAGE_ROUTE} data-testid="unload-footer-back">
            <FooterItem footerItemText={AppStrings.UNLOAD_CHOOSER_FOOTER_HOME} footerItemTokenIdentifier="back"/>
        </Link>
    </div>
) };

interface UnloadChooserPageProps {
    // The tools the associate may use
    accessibleTools: string[];
}

// /unload: one tap opens Receive or any unload flow
const UnloadChooserPage = ({ accessibleTools }: UnloadChooserPageProps) => {
    useSetPageChrome(CHROME);
    React.useEffect(lockZoomOnSmallScreens, []);
    const options = accessibleTools.includes(RECEIVE_TOOL) ? [RECEIVE, ...UNLOAD_OPTIONS] : UNLOAD_OPTIONS;
    return (
        <div className="unload unload-chooser">
            {/* b500 is every mode's title above the scanner width; below it all text is 12px */}
            <div data-testid="unload-chooser-heading">
                <Text type="b500" alignment="center">{AppStrings.UNLOAD_CHOOSER_HEADING}</Text>
            </div>
            {options.map(option => (
                <Link key={option.testId} to={option.to} data-testid={option.testId} className="unload-chooser-option">
                    <Text type="h100">{option.name}</Text>
                    <Text type="b200" color="secondary" className="unload-chooser-line">{option.line}</Text>
                </Link>
            ))}
        </div>
    );
};

export default UnloadChooserPage;
